import { AnimatePresence, motion } from "motion/react";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";

import { CollapseIcon } from "@ryde/controls";
import { useAuditLogContext, useSelectItemsTranslate } from "@ryde/redux";
import {
  hasKeys,
  IAuditLogNormalized,
  onActivateKeyDown,
  useFormats,
} from "@ryde/shared";

import { RideHistoryLogGridCell } from "./grid-cell";
import { RideHistoryLogGridRowDetails } from "./grid-row-details";
import { getGetterByKey } from "./grid.helpers";
import {
  expandVariants,
  noNeedToCheckPrevKeys,
  RideHistoryHeaderKeys,
  rideHistoryHeaders,
} from "./grid.models";

interface IRideHistoryLogGridRowProps {
  item: IAuditLogNormalized;
  prevItem: IAuditLogNormalized | null;
  isExpanded?: boolean;
  onToggleExpanded?: (id: number) => void;
}

export const RideHistoryLogGridRow: FC<IRideHistoryLogGridRowProps> = ({
  item,
  prevItem,
  isExpanded: externalIsExpanded,
  onToggleExpanded,
}) => {
  const [internalIsExpanded, setInternalIsExpanded] = useState(false);
  const { searchValue: search } = useAuditLogContext();
  const { formatDateTimeNormalized, formatDateTimeWithSecondsNormalized } =
    useFormats();
  const { t } = useTranslation();
  const { translateLabel } = useSelectItemsTranslate();

  const isExpanded =
    externalIsExpanded !== undefined ? externalIsExpanded : internalIsExpanded;

  const toggleExpanded = (): void => {
    if (onToggleExpanded) {
      onToggleExpanded(item.id);
    } else {
      setInternalIsExpanded(prev => !prev);
    }
  };

  return (
    <div>
      <div
        className={`ride-history-log__grid-row ${isExpanded ? "ride-history-log__grid-row--expanded" : ""}`}
        role="button"
        tabIndex={0}
        onClick={toggleExpanded}
        onKeyDown={onActivateKeyDown(toggleExpanded)}
      >
        <div className="ride-history-log__grid-cell ride-history-log__grid-cell_expanded">
          <CollapseIcon isCollapsed={isExpanded} variant="quaternary" />
        </div>
        {Object.keys(rideHistoryHeaders).map(key => {
          const getter = getGetterByKey(key as RideHistoryHeaderKeys);
          const value = getter({
            item,
            formatDateTimeNormalized,
            formatDateTimeWithSecondsNormalized,
            translateLabel,
            translationFn: t,
          });
          const prevValue =
            prevItem && hasKeys(prevItem)
              ? getter({
                  item: prevItem,
                  formatDateTimeNormalized,
                  formatDateTimeWithSecondsNormalized,
                  translationFn: t,
                  translateLabel,
                })
              : null;

          return (
            <RideHistoryLogGridCell
              key={key}
              value={value}
              prevValue={prevValue}
              isNeedToCheckPrev={!noNeedToCheckPrevKeys.includes(key)}
              search={search}
            />
          );
        })}
      </div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            className="ride-history-log__grid-row-details"
            variants={expandVariants}
            initial="collapsed"
            animate="expanded"
            exit="collapsed"
          >
            <RideHistoryLogGridRowDetails item={item} prevItem={prevItem} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
